import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import DOMPurify from "dompurify";
import {
  X, Gear, Robot, PaperPlaneRight, WarningCircle, Sparkle,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";
// import { toPng } from "html-to-image";
// import jsPDF from "jspdf";
import { DownloadSimple } from "@phosphor-icons/react";

import {
  PieChart, Pie, Cell, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer,
} from "recharts";

/* 
   FinanceAgentModal:
   
   Isolated modal for the finance "chat-only" agents that used to be
   special-cased inside AgentRunModal via CHAT_ONLY_PATTERNS / isChatOnlyAgent():

     - account-overview        (aliases: account_overview, finance-account-overview)
     - finance-variance        (alias:   finance-account-variance)
     - finance-future-insight  (alias:   future-insight)
     - finance-narrative       (alias:   finance-reporting-narrative)

   ALL FOUR ARE CHAT-ONLY AGENTS. That means, matching AgentRunModal's
   isChatOnlyAgent()/isChatOnlyWizard behavior exactly:

     - No Send-to-Oracle / Dry-run-validate / Submit-anyway buttons —
       these agents never write to Oracle, they only answer questions.
     - The wizard panel on the right IS still shown, but purely as a
       CONTEXT/filter panel (e.g. account, period, cost center) — values
       picked there are folded into the question text via
       buildFieldContext() rather than submitted anywhere.
     - The modal opens at the wide "chat-only" size (matches
       AgentRunModal's `.modal-chat-wide`), not the narrower default chat
       size used by wizard-driven agents.
     - No attachments, no PO/confirm popups, no Copilot progress bar —
       none of that applies to a read-only Q&A agent.

   They post `{ session_id, question }` to a simple `/chat` endpoint and
   render the `answer` back (markdown for three of them, sanitized raw
   HTML for finance-variance — see ChatBubble below).

   WIRING IT UP:
  
   In AgentRunModal.jsx
   import FinanceAgentModal, { isFinanceAgent } from "@/components/erp/FinanceAgentModal";
     ...
     if (isFinanceAgent(openAgent)) {
       return <FinanceAgentModal />;
     }

   That one addition removes the need for the account-overview /
   finance-variance / finance-future-insight / finance-narrative branches
   inside AgentRunModal's sendMessage, and removes these four keys from
   CHAT_ONLY_PATTERNS there too — this file owns all of that now. */

/* ── Agent registry — exact patterns from CHAT_ONLY_PATTERNS, grouped ──── */
export const FINANCE_AGENTS = [
  {
    id: "account-overview",
    label: "Account Overview",
    hint: "Ask about balances, activity, and account-level detail.",
    keys: ["account-overview", "finance-account-overview"],
    matchIncludes: ["account-overview", "account_overview"],
    endpoint: "/erp-agents/account-overview/chat",
  },
  {
    id: "finance-variance",
    label: "Finance Variance",
    hint: "Ask about budget-to-actual variance and drivers.",
    keys: ["finance-variance", "finance-account-variance"],
    matchIncludes: ["finance-variance"],
    endpoint: "/erp-agents/finance-variance/chat",
  },
  {
    id: "finance-future-insight",
    label: "Finance Future Insight",
    hint: "Ask about forward-looking projections and trends.",
    keys: ["finance-future-insight", "future-insight"],
    matchIncludes: ["finance-future"],
    endpoint: "/erp-agents/future-insights/chat",
  },
  {
    id: "finance-narrative",
    label: "Finance Reporting Narrative",
    hint: "Ask for a written narrative summary of financial results.",
    keys: ["finance-narrative", "finance-reporting-narrative"],
    matchIncludes: ["finance-narrative"],
    endpoint: "/erp-agents/finance-narrative/chat",
    hasDashBoard: true,
  },
];

/** Resolve which finance agent (and endpoint) an openAgent key maps to. */
export function resolveFinanceAgent(openAgent) {
  if (!openAgent) return null;
  return (
    FINANCE_AGENTS.find(
      (agent) =>
        agent.keys.includes(openAgent) ||
        agent.matchIncludes.some((substr) => openAgent.includes(substr))
    ) || null
  );
}

/** True if openAgent is one of the four finance chat-only agents. */
export function isFinanceAgent(openAgent) {
  return !!resolveFinanceAgent(openAgent);
}

/* Every agent in this file is chat-only by definition — kept as its own
   helper (mirroring AgentRunModal's isChatOnlyAgent) so call sites read
   the same way they did there, and so this stays true even if the
   registry above grows to include a non-chat-only finance agent later. */
function isChatOnlyAgent(openAgent) {
  return isFinanceAgent(openAgent);
}

/* ── Context-field formatting, ported from AgentRunModal ────────────────
   AgentRunModal referenced a `formatDateForContext` helper that isn't
   defined anywhere in that file (it would throw if a date-type wizard
   field were ever filled in). Defining it properly here so the context
   panel works for date fields too. */
function formatDateForContext(raw) {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** Folds the wizard/context field values into a short "Field: value, …"
 *  string that gets prepended to the user's question — e.g. so "what
 *  drove the variance?" becomes "Account: 6100 · Period: Q2 2026. what
 *  drove the variance?" once the user has picked filters on the right. */
function buildFieldContext(fieldValues, schemaFields) {
  if (!schemaFields?.length) return "";
  return schemaFields
    .map((f) => {
      const raw = fieldValues[f.name];
      if (!raw) return null;
      if (f.type === "date") return `${f.label}: ${formatDateForContext(raw)}`;
      const label = fieldValues[`${f.name}__label`];
      return `${f.label}: ${label || raw}`;
    })
    .filter(Boolean)
    .join(", ");
}

/** Reads the auth token out of storage the same way lib/api.js's axios
 *  interceptor does, so `fetch` (which never runs that interceptor) can
 *  attach it manually.
 *
 *  Matches lib/api.js exactly:
 *    const token = localStorage.getItem("token");
 *    if (token) config.headers.Authorization = `Bearer ${token}`; */
function getStoredAuthToken() {
  return localStorage.getItem("treasury_token");
}

/** Streams an SSE-framed response where every event's `data:` payload is
 *  a JSON object — `{"type": "token", "content": "..."}` for answer
 *  tokens, `{"type": "status", "content": "..."}` for the backend's
 *  in-progress status updates (from emit_status() on the graph side).
 *  Calls `onEvent(parsedEvent)` once per completed event, in order.
 *
 *  Since the backend now does `json.dumps(event)` before framing it as
 *  `data: {json}\n\n`, every event's payload is guaranteed to be a single
 *  physical line — json.dumps escapes any real newline inside a string
 *  as the two characters `\n`, it never emits a literal newline byte.
 *  That actually fixes the older raw-text-streaming bug at the source
 *  (a literal newline inside a token used to collide with the SSE
 *  blank-line terminator and get silently dropped) — but the multi-line
 *  event buffering below is kept anyway as a harmless safety net in case
 *  any event ever legitimately spans more than one `data:` line.
 *
 *  Bypasses the `api` axios instance for this one call, since axios can't
 *  stream cleanly in the browser. That also means axios's request
 *  interceptor (the one that normally attaches "Authorization: Bearer
 *  <token>") never runs — see getStoredAuthToken() below, which reads the
 *  token straight out of storage and attaches it here instead. */
async function streamEvents(endpoint, payload, onEvent, signal) {
  const base = api.defaults?.baseURL || "";
  const authHeaders = { ...(api.defaults?.headers?.common || {}) };
  delete authHeaders["Content-Type"]; // set explicitly below

  const token = getStoredAuthToken();
  if (token) authHeaders["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${base}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    throw new Error(errBody || `Request failed (${res.status})`);
  }

  let pendingEventLines = [];

  const flushPendingEvent = () => {
    if (pendingEventLines.length === 0) return;
    const raw = pendingEventLines.join("\n");
    pendingEventLines = [];
    if (!raw) return;
    try {
      onEvent(JSON.parse(raw));
    } catch {
      // Not valid JSON — ignore rather than crash the stream over one
      // malformed event (e.g. a stray keep-alive comment line).
    }
  };

  const consumeLine = (line) => {
    if (line === "") {
      flushPendingEvent(); // blank line = SSE event boundary
      return;
    }
    if (!line.startsWith("data:")) return; // ignore other SSE fields (event:, id:, retry:)
    pendingEventLines.push(line.slice(5).replace(/^ /, ""));
  };

  if (!res.body) {
    // Environment without streaming body support — parse the whole SSE
    // body at once using the same line-by-line reconstruction.
    const wholeBody = await res.text();
    for (const line of wholeBody.split("\n")) consumeLine(line);
    flushPendingEvent();
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let lineBuffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    lineBuffer += decoder.decode(value, { stream: true });
    const lines = lineBuffer.split("\n");
    lineBuffer = lines.pop() ?? ""; // last, possibly-incomplete line stays buffered
    for (const line of lines) consumeLine(line);
  }
  if (lineBuffer) consumeLine(lineBuffer); // flush a final line with no trailing newline
  flushPendingEvent(); // flush a final event that never got a closing blank line
}

/** The backend prefixes every streamed answer with a small JSON blob
 *  before the real text — e.g. `{"Intent":"greeting"}Hello! How can I
 *  help...` — streamed token-by-token like everything else. That's
 *  metadata, not part of the answer, so this strips a leading *complete*
 *  JSON object (brace-balance aware, so nested braces don't confuse it)
 *  and returns the remainder. While the JSON is still arriving (braces
 *  not yet balanced), returns "" so nothing renders until the real
 *  answer text starts — avoids flashing partial `{"Inte` on screen. */
function stripLeadingJsonPrefix(raw) {
  const trimmed = raw.replace(/^\s+/, "");
  if (!trimmed.startsWith("{")) return raw;

  let depth = 0;
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] === "{") depth++;
    else if (trimmed[i] === "}") {
      depth--;
      if (depth === 0) return trimmed.slice(i + 1).replace(/^\s+/, "");
    }
  }
  return ""; // JSON prefix started but hasn't closed yet — keep buffering
}

/* ── Markdown renderer for assistant messages ────────────────────────────
   Same styling as AgentRunModal's ChatMarkdown so bubbles look identical. */
function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li: ({ node, ...props }) => <li className="text-sm" {...props} />,
          h1: ({ node, ...props }) => <h1 className="text-base font-bold mb-1 mt-2" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-sm font-bold mb-1 mt-2" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...props} />,
          code: ({ node, inline, ...props }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...props} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...props} />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table className="w-full text-xs border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-[#0020C2]/5" {...props} />,
          th: ({ node, ...props }) => (
            <th className="border border-gray-200 px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[10px] text-gray-600" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-gray-200 px-2 py-1.5 text-gray-800" {...props} />
          ),
          a: ({ node, ...props }) => <a className="text-[#0020C2] underline" {...props} />,
          hr: () => <hr className="my-2 border-gray-200" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

/* ── Chat bubble ───────────────────────────────────────────────────────
   Finance agents never send next_field_ui, so this bubble only needs to
   render text plus a loading state — but WHICH renderer depends on the
   agent:

     - finance-variance          → the LLM returns raw HTML (tables,
                                    headings, etc). We sanitize it with
                                    DOMPurify and render it directly via
                                    dangerouslySetInnerHTML, same as the
                                    original AgentRunModal ChatBubble did
                                    for `openAgent === "finance-variance"`.
     - everything else (the other
       three finance agents)     → plain markdown via ChatMarkdown, since
                                    those return markdown-formatted text,
                                    not HTML.

   `agentId` is FINANCE_AGENTS[].id for the currently open agent, passed
   down from FinanceAgentModal so this component doesn't need to read
   openAgent itself. */


function trendArrow(trend) {
  if (trend === "up") return "↑";
  if (trend === "down") return "↓";
  return "→";
}

const PIE_COLORS = ["#0020C2", "#16A34A", "#DC2626", "#F59E0B", "#8B5CF6", "#0891B2", "#DB2777", "#65A30D"];

function BudgetCompositionPie({ slices }) {
  if (!slices?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-2">
        Budget Composition
      </div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={80}
              paddingAngle={2}
            >
              {slices.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(val) => Number(val).toLocaleString()} />
            <Legend
              layout="horizontal"
              align="bottom"
              verticalAlign="bottom"
              wrapperStyle={{ fontSize: 11 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function VarianceBar({ item, maxAbsPct }) {
  const pctNum = parseFloat((item.variance_pct || "0").replace(/[^\d.-]/g, "")) || 0;
  const widthPct = maxAbsPct ? Math.min(100, (Math.abs(pctNum) / maxAbsPct) * 100) : 0;
  const isFavorable = item.status === "favorable";
  const isUnfavorable = item.status === "unfavorable";
  const barColor = isFavorable ? "#16A34A" : isUnfavorable ? "#DC2626" : "#9CA3AF";

  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold text-gray-800">{item.account_or_category}</span>
        <span className="text-gray-500">
          Budget {item.budget_amount} · Actual {item.actual_amount}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: `${widthPct}%`, backgroundColor: barColor }}
          />
        </div>
        <span
          className="text-[11px] font-bold shrink-0"
          style={{ color: barColor }}
        >
          {item.variance_pct || item.variance_amount}
        </span>
      </div>
    </div>
  );
}

function heatColor(pctNum, isFavorable, isUnfavorable) {
  const intensity = Math.min(1, Math.abs(pctNum) / 30);
  const mix = (from, to) => Math.round(from + (to - from) * intensity);

  if (isFavorable) {
    // #DCFCE7 (green-100) → #16A34A (green-600)
    return `rgb(${mix(220, 22)}, ${mix(252, 163)}, ${mix(231, 74)})`;
  }
  if (isUnfavorable) {
    // #FEE2E2 (red-100) → #DC2626 (red-600)
    return `rgb(${mix(254, 220)}, ${mix(226, 38)}, ${mix(226, 38)})`;
  }
  return "#E5E7EB"; // neutral gray
}

function VarianceHeatGrid({ variances }) {
  if (!variances?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-3">
        Budget vs Actual — Variance Heatmap
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
        {variances.map((v, i) => {
          const parsed = parseFloat((v.variance_pct || "").replace(/[^\d.-]/g, ""));
          const pctNum = Number.isFinite(parsed) ? parsed : 15;
          const isFavorable = v.status === "favorable";
          const isUnfavorable = v.status === "unfavorable";
          const bg = heatColor(pctNum, isFavorable, isUnfavorable);
          return (
            <div
              key={i}
              title={`${v.account_or_category}\nBudget: ${v.budget_amount}\nActual: ${v.actual_amount}\nVariance: ${v.variance_pct || v.variance_amount}`}
              className="rounded-lg p-2 flex flex-col justify-between h-16 cursor-default transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: bg }}
            >
              <span className="text-[10px] font-bold text-gray-800 truncate">
                {v.account_or_category}
              </span>
              <span className="text-xs font-black text-gray-900">
                {v.variance_pct || v.variance_amount}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-3 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: "rgb(80,220,60)" }} /> Favorable</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: "rgb(240,60,60)" }} /> Unfavorable</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block bg-gray-300" /> Neutral</span>
      </div>
    </div>
  );
}


function BudgetActualTrendChart({ points }) {
  if (!points || points.length < 2) return null; // need ≥2 periods for a meaningful trend
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-2">
        Budget vs Actual Trend
      </div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <LineChart data={points} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => Intl.NumberFormat("en", { notation: "compact" }).format(v)} />
            <RLTooltip formatter={(val) => Number(val).toLocaleString()} />
            <RLLegend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="budget" name="Budget" stroke="#0020C2" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="actual" name="Actual" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function FinanceDashboard({ data }) {
  if (!data) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400 px-6 text-center">
        No dashboard data yet — ask a question in Chat to generate one.
      </div>
    );
  }
   const maxAbsPct = Math.max(
    1,
    ...(data.variances || []).map(
      (v) => Math.abs(parseFloat((v.variance_pct || "0").replace(/[^\d.-]/g, "")) || 0)
    )
  );

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 bg-gray-50">
      {data.executive_summary && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-1.5">
            Executive Summary
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">{data.executive_summary}</p>
        </div>
      )}

      {data.kpis?.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {data.kpis.map((kpi, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
                {kpi.label}
              </div>
              <div className="flex items-baseline gap-1.5 mt-1">
                <span className="text-lg font-black text-gray-900">{kpi.value}</span>
                {kpi.trend_pct && (
                  <span
                    className={`text-xs font-bold ${
                      kpi.trend === "up" ? "text-green-600" : kpi.trend === "down" ? "text-red-600" : "text-gray-400"
                    }`}
                  >
                    {trendArrow(kpi.trend)} {kpi.trend_pct}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <BudgetCompositionPie slices={data.budget_composition} />
      {/* <VarianceHeatGrid variances={data.variances} /> */}
      {data.variances?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-2">
            Budget vs Actual Variance
          </div>
          {data.variances.map((v, i) => (
            <VarianceBar key={i} item={v} maxAbsPct={maxAbsPct} />
          ))}
        </div>
      )}
      <BudgetActualTrendChart points={data.trend_series} />

      {["trends", "risks", "recommendations"].map((key) =>
        data[key]?.length > 0 ? (
          <div key={key} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-2 capitalize">
              {key}
            </div>
            <ul className="list-disc pl-5 space-y-1">
              {data[key].map((line, i) => (
                <li key={i} className="text-sm text-gray-700">{line}</li>
              ))}
            </ul>
          </div>
        ) : null
      )}
    </div>
  );
}


function ChatBubble({ msg, agentId }) {
  const isUser = msg.role === "user";
  const isFinanceVariance = agentId === "finance-variance";

  return (
    <>
      <style>{`
     @keyframes financeStatusFadeIn { from { opacity:0; transform:translateY(2px); } to { opacity:1; transform:translateY(0); } }
     `}</style>
      <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`} data-testid={`chat-bubble-${msg.role}`}>
        {!isUser && (
          <div className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-[#0020C2] text-white">
            <Sparkle size={11} weight="fill" />
          </div>
        )}

        <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
        ${isUser
            ? "bg-[#0020C2] text-white rounded-tr-sm"
            : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"}`}>

          {!msg.text && msg.statusText && (
            <div className="py-0.5 text-gray-500">
              <span
                key={msg.statusText}
                className="
        text-sm italic
        bg-gradient-to-r
        from-gray-400
        via-gray-700
        to-gray-400
        bg-[length:200%_100%]
        bg-clip-text
        text-transparent
        animate-shimmer
      "
              >
                {msg.statusText}
              </span>
            </div>
          )}

          {!msg.text && !msg.statusText && (
            <div className="flex items-center gap-1 py-0.5">
              {[0, 150, 300].map((d) => (
                <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: `${d}ms` }} />
              ))}
            </div>
          )}

          {msg.text && (
            isUser ? (
              <p className="whitespace-pre-wrap">{msg.text}</p>
            ) : isFinanceVariance ? (
              <div
                className="
                prose
                prose-sm
                max-w-none
                whitespace-pre-line
                prose-p:my-2
                prose-h2:mt-4
                prose-h2:mb-2
                prose-ul:my-2

                [&_table]:w-1/3
                [&_table]:border
                [&_table]:border-collapse
                [&_table]:my-3

                [&_thead]:bg-gray-100

                [&_th]:border
                [&_th]:border-gray-300
                [&_th]:px-3
                [&_th]:py-2
                [&_th]:text-left
                [&_th]:font-semibold

                [&_td]:border
                [&_td]:border-gray-300
                [&_td]:px-3
                [&_td]:py-2

                [&_tr:nth-child(even)]:bg-gray-50
              "
                data-testid="finance-variance-html"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(msg.text),
                }}
              />
            ) : (
              <ChatMarkdown>{msg.text}</ChatMarkdown>
            )
          )}

          {msg.timestamp && (
            <div className={`text-[10px] mt-1 ${isUser ? "text-white/60" : "text-gray-400"}`}>
              {msg.timestamp}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Input bar — send box, no attach button (finance agents are text-only) */
function InputBar({ inputRef, value, onChange, onKeyDown, onSend, busy, placeholder }) {
  return (
    <div className="border-t border-gray-200 p-3 bg-white shrink-0">
      <div className="flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={1}
          className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2.5 text-sm
            focus:outline-none focus:border-[#0020C2] max-h-[120px]"
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          data-testid="finance-agent-send"
          className="shrink-0 bg-[#0020C2] text-white rounded-xl w-10 h-10 flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors">
          {busy
            ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={15} weight="fill" />}
        </button>
      </div>
    </div>
  );
}

/* ── Main modal ───────────────────────────────────────────────────────── */
export default function FinanceAgentModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents, launchAgent } = useErp();

  const financeAgent = resolveFinanceAgent(openAgent);
  const agentMeta = agents?.find((a) => a.key === openAgent);

  // isChatOnly stays true for the whole lifetime of this component (every
  // agent it renders is chat-only) — kept as a variable rather than a
  // hardcoded `true` so the render below reads exactly like AgentRunModal's
  // isChatOnlyWizard checks did.
  // const isChatOnly = isChatOnlyAgent(openAgent);

  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [chatStarted, setChatStarted] = React.useState(false);
  const [sessionId, setSessionId] = React.useState(() => crypto.randomUUID());
  const [loadError, setLoadError] = React.useState("");
  const [dashboardData, setDashboardData] = React.useState(null);
  const [viewMode, setViewMode] = React.useState("chat");
  const dashboardRef = React.useRef(null);
  const [downloadingPdf, setDownloadingPdf] = React.useState(false);

  // ── Context/filter fields — the right-hand panel. These are NEVER
  // submitted to Oracle; they only feed buildFieldContext() below so the
  // question sent to the LLM carries whatever filters the user picked
  // (account, period, cost center, etc). ──
  const [fieldValues, setFieldValues] = React.useState({});
  const [missingFields, setMissingFields] = React.useState([]);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [fieldWarnings, setFieldWarnings] = React.useState({});
  const [wizardSchemaFields, setWizardSchemaFields] = React.useState([]);
  const lastSentContextRef = React.useRef("");

  const messagesEndRef = React.useRef(null);
  const chatContainerRef = React.useRef(null);
  const shouldAutoScrollRef = React.useRef(true);
  const inputRef = React.useRef(null);
  const abortControllerRef = React.useRef(null);
  const typewriterRef = React.useRef(null);

  // Reset local state whenever the selected agent changes.
  React.useEffect(() => {
    setMessages([]);
    setInput("");
    setBusy(false);
    setChatStarted(false);
    setLoadError("");
    setSessionId(crypto.randomUUID());
    setFieldValues({});
    setMissingFields([]);
    setFieldErrors({});
    setFieldWarnings({});
    setWizardSchemaFields([]);
    setDashboardData(null);
    setViewMode("chat");
    lastSentContextRef.current = "";
    abortControllerRef.current?.abort();
    stopTypewriter();
  }, [openAgent]);

  React.useEffect(() => {
    if (shouldAutoScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [messages]);

  // Tracks whether the user is (still) near the bottom of the chat, so
  // the effect above only auto-scrolls when they haven't deliberately
  // scrolled up to read something earlier. Without this, every typewriter
  // tick (every 20ms while streaming) yanks the view back to the bottom,
  // which makes manual scrolling feel broken/impossible.
  const handleChatScroll = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollRef.current = distanceFromBottom < 50; // px threshold
  };

  const handleUserScrollIntent = () => {
    shouldAutoScrollRef.current = false;
  };

  // Tune these two to taste: bigger CHARS_PER_TICK or smaller INTERVAL_MS
  // = faster perceived typing. Declared above the early-return guard below
  // (along with stopTypewriter/startTypewriter) so they're always
  // initialized even on a render where this component briefly has no
  // matching finance agent — the reset effect above calls stopTypewriter()
  // unconditionally, before that guard runs.
  const TYPEWRITER_CHARS_PER_TICK = 5;
  const TYPEWRITER_INTERVAL_MS = 20;

  const stopTypewriter = () => {
    if (typewriterRef.current) {
      clearInterval(typewriterRef.current);
      typewriterRef.current = null;
    }
  };

  // Reveals `fullText` into `text` a few characters at a time instead of
  // slamming the whole chunk in as soon as it arrives — that's what made
  // it feel too fast, since the raw SSE chunks can arrive in fast bursts.
  const startTypewriter = (assistantId, isStreamDone) => {
    stopTypewriter();
    typewriterRef.current = setInterval(() => {
      let caughtUp = false;
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantId) return m;
          const target = m.fullText ?? "";
          if (m.text.length >= target.length) {
            caughtUp = true;
            return m;
          }
          const nextLen = Math.min(target.length, m.text.length + TYPEWRITER_CHARS_PER_TICK);
          return { ...m, text: target.slice(0, nextLen) };
        })
      );
      if (caughtUp && isStreamDone.current) {
        stopTypewriter();
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, streaming: false, timestamp: ts() } : m))
        );
      }
    }, TYPEWRITER_INTERVAL_MS);
  };

  // Not one of our four agents — render nothing so the caller (AgentRunModal)
  // can fall through to its own modal for every other agent.
  if (!openAgent || !financeAgent) return null;

  const ts = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const close = () => {
    abortControllerRef.current?.abort();
    stopTypewriter();
    setOpenAgent(null);
    setMessages([]);
    setChatStarted(false);
    setFieldValues({});
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  /* ── Send message — simple Q&A endpoint per finance agent family ────
     Mirrors AgentRunModal's sendMessage: since isChatOnlyAgent(openAgent)
     is always true here, we always fold the context panel's field values
     into the outgoing question — but only when that context is new/changed
     since the last turn, so it isn't repeated on every message.

     STREAMING: streamEvents() parses each SSE event's JSON payload and
     dispatches it here. Two event types come through:
       - {"type": "status", "content": "..."}  → a live progress update
         from the backend graph (e.g. "Fetching ledger accounts…"). Shown
         in place of a generic 3-dot spinner while we wait.
       - {"type": "token", "content": "..."}    → a chunk of the actual
         answer. Accumulated into `fullText` (the target); the typewriter
         interval above reveals it into the displayed `text` gradually. */

  
    const handleDownloadDashboardPdf = () => {
    const node = dashboardRef.current;
    if (!node || downloadingPdf) return;
    setDownloadingPdf(true);

    let iframe = null;

    try {
      // Copy every stylesheet link + inline <style> from the app so the
      // print document gets the real Tailwind CSS — this is the step that
      // html-to-image was effectively failing at.
      const headMarkup = Array.from(
        document.querySelectorAll('link[rel="stylesheet"], style')
      )
        .map((el) => el.outerHTML)
        .join("\n");

      iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${(title || "Finance Dashboard")} — Dashboard</title>
  ${headMarkup}
  <style>
    body { margin: 0; background: #F9FAFB; }
    .print-root { padding: 20px; background: #F9FAFB; }
    /* Never split a card across a page boundary. */
    .print-root > div > * { break-inside: avoid; page-break-inside: avoid; }
    @page { size: A4; margin: 12mm; }
  </style>
</head>
<body>
  <div class="print-root">${node.innerHTML}</div>
</body>
</html>`);
      doc.close();

      // Wait for the copied stylesheets (and any fonts) to load inside the
      // iframe before printing, otherwise it prints unstyled.
      const win = iframe.contentWindow;
      const doPrint = () => {
        win.focus();
        win.print();
        // Give the print dialog a moment before tearing the iframe down.
        setTimeout(() => {
          if (iframe && iframe.parentNode) document.body.removeChild(iframe);
          setDownloadingPdf(false);
        }, 1000);
      };

      if (win.document.readyState === "complete") {
        setTimeout(doPrint, 400);
      } else {
        win.addEventListener("load", () => setTimeout(doPrint, 400));
      }
    } catch (err) {
      console.error("Dashboard print failed:", err);
      if (iframe && iframe.parentNode) document.body.removeChild(iframe);
      setDownloadingPdf(false);
    }
  };


  const sendMessage = async (text) => {
    const rawText = (text || "").trim();
    if (!rawText || busy) return;

    const fullCtx = isChatOnlyAgent(openAgent) ? buildFieldContext(fieldValues, wizardSchemaFields) : "";
    const ctx = fullCtx && fullCtx !== lastSentContextRef.current ? fullCtx : "";
    const question = ctx ? `${ctx}. ${rawText}` : rawText;
    if (fullCtx) lastSentContextRef.current = fullCtx;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);
    shouldAutoScrollRef.current = true;

    // crypto.randomUUID() rather than Date.now(): two Date.now() calls a
    // few lines apart can land on the same millisecond, which previously
    // gave the user message and the assistant message the SAME id — the
    // streaming update below then matched and overwrote both, wiping out
    // the user's own text (what you're seeing in the screenshot).
    const userId = crypto.randomUUID();
    const assistantId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: userId, role: "user", text: question, timestamp: ts() },
      { id: assistantId, role: "assistant", text: "", fullText: "", statusText: "", streaming: true },
    ]);
    setBusy(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const isStreamDone = { current: false };
    startTypewriter(assistantId, isStreamDone);

    let accumulatedTokens = "";

    try {
      await streamEvents(
        financeAgent.endpoint,
        { session_id: sessionId, question },
        (event) => {
          if (event?.type === "status") {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, statusText: event.content || "" } : m))
            );
          } else if (event?.type === "token") {
            accumulatedTokens += event.content || "";
            // stripLeadingJsonPrefix is a safety net for an older backend
            // quirk (an intent-classification JSON blob mixed into the
            // token stream) — harmless to keep even if this event
            // structure no longer produces that, since it only strips
            // anything at all when the accumulated text starts with "{".
            const displayText = stripLeadingJsonPrefix(accumulatedTokens);
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, fullText: displayText } : m))
            );
          } else if (event?.type === "dashboard") {           // NEW
            if (event.content) setDashboardData(event.content);
          }
        },
        controller.signal
      );
      isStreamDone.current = true; // typewriter will flip streaming:false once it catches up
    } catch (e) {
      if (e?.name === "AbortError") {
        // Modal was closed / agent switched mid-stream — drop silently,
        // there's no message left in the UI to update.
        stopTypewriter();
        return;
      }
      stopTypewriter();
      const errText = `Error: ${e.message}`;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, streaming: false, timestamp: ts(), text: errText, fullText: errText, statusText: "" }
            : m
        )
      );
    } finally {
      setBusy(false);
      abortControllerRef.current = null;
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const title = agentMeta?.label || financeAgent.label;
  const subtitle = agentMeta?.hint || financeAgent.hint;

  /* Context/filter panel — right side. No Send-to-Oracle / Dry-run /
     Submit-anyway buttons: this agent family never writes anything, so
     those buttons (and the wizardResult banner that goes with them) are
     simply never rendered here, matching AgentRunModal hiding them
     whenever isChatOnlyWizard is true. */
  const ContextPanel = (
    <div className="flex flex-col bg-white h-full" style={{ minWidth: "300px" }}>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
          <Sparkle size={11} weight="fill" /> Context filters
          <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
            · narrows down what you ask
          </span>
        </div>

        <WizardFields
          agentKey={openAgent}
          values={fieldValues}
          onChange={setFieldValues}
          missingFields={missingFields}
          onCreateNew={(key) => { close(); setTimeout(() => launchAgent?.(key), 100); }}
          fieldErrors={fieldErrors}
          fieldWarnings={fieldWarnings}
          onSchemaLoad={setWizardSchemaFields}
        />

        {Object.keys(fieldErrors).length > 0 && (
          <div className="border border-[#DC2626] bg-red-50 px-3 py-2 text-xs text-[#DC2626] flex items-start gap-2">
            <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
            <span className="font-bold">
              {Object.keys(fieldErrors).length} field(s) need attention above.
            </span>
          </div>
        )}
      </div>
      {/* No bottom button bar here on purpose — chat-only agents don't submit anything. */}
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes financeChatFadeIn    { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes financeWizardSlideIn { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        .finance-modal-pre       { width:672px; max-width:calc(100vw - 2rem); max-height:90vh; }
        /* Chat-only agents always open at the WIDE size — matches
           AgentRunModal's .modal-chat-wide, used whenever isChatOnlyWizard
           is true. */
        .finance-modal-chat-wide {
          width:min(96vw,1600px); height:95vh;
          resize: both; overflow: auto;
          min-width: 640px; min-height: 460px;
          max-width: 96vw;  max-height: 94vh;
        }
        .finance-modal-chat-wide::-webkit-resizer { display: none; }
        .finance-modal-resize-hint {
          position: absolute; right: 3px; bottom: 3px; width: 14px; height: 14px;
          cursor: nwse-resize; pointer-events: none; opacity: 0.5;
        }
      `}</style>

      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={() => { if (!chatStarted) close(); }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`finance-agent-modal-${openAgent}`}
          className={`relative bg-white text-gray-900 border border-gray-300 flex flex-col
            ${chatStarted ? "finance-modal-chat-wide" : "finance-modal-pre overflow-hidden"}`}
          style={{ transition: chatStarted ? "none" : "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {chatStarted && (
            <svg className="finance-modal-resize-hint" viewBox="0 0 14 14" fill="none">
              <path d="M13 5 L5 13 M13 9 L9 13 M13 1 L1 13" stroke="#9CA3AF" strokeWidth="1.2" />
            </svg>
          )}

          {/* ── Header ── */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI · Finance (chat-only)
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => { close(); setSetupOpen(true); }}
                data-testid="finance-agent-open-profile"
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="finance-agent-modal-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          {!chatStarted ? (
            /* PRE-CHAT — compact modal: context filters + input, NO
               Send-to-Oracle / Dry-run buttons, since this agent family
               never writes anything (isChatOnlyWizard-equivalent). */
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className="border border-[#0020C2]/20 bg-[#0020C2]/5 rounded-lg px-3 py-2.5 text-xs text-gray-600 flex items-start gap-2">
                  <Sparkle size={13} weight="fill" className="text-[#0020C2] mt-0.5 shrink-0" />
                  <span>
                    Ask a question in plain language — e.g. "{subtitle}". Optionally set
                    filters below first; they'll be folded into your question automatically.
                    This agent answers directly — there's nothing to submit.
                  </span>
                </div>

                <WizardFields
                  agentKey={openAgent}
                  values={fieldValues}
                  onChange={setFieldValues}
                  missingFields={missingFields}
                  onCreateNew={(key) => { close(); setTimeout(() => launchAgent?.(key), 100); }}
                  fieldErrors={fieldErrors}
                  fieldWarnings={fieldWarnings}
                  onSchemaLoad={setWizardSchemaFields}
                />

                {loadError && (
                  <div className="border border-[#DC2626] bg-red-50 px-3 py-2 text-xs text-[#DC2626] flex items-start gap-2">
                    <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
                    <span className="font-bold">{loadError}</span>
                  </div>
                )}
              </div>
              <div className="border-t border-gray-200 px-4 py-2 bg-white shrink-0">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Robot size={12} weight="bold" />
                  <span>Type a message below to start the conversation</span>
                </div>
              </div>
              <InputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
                placeholder={subtitle || "Ask a question…"}
              />
              {/* No button bar here on purpose — chat-only agents don't submit anything. */}
            </>
          ) : (
            /* CHAT MODE — 60% chat left / 40% context-filters right,
               matching AgentRunModal's chat-only-wizard layout exactly
               (wide modal, no submit buttons on the right panel). */
            <div className="flex flex-1 overflow-hidden">
              <div
                className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{ width: "60%", animation: "financeChatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}>

                {/* Chat / Dashboard toggle + download button */}
                {financeAgent.hasDashBoard && (<div className="flex items-center justify-between px-3 pt-2.5 pb-1 shrink-0 bg-white border-b border-gray-100">
                  <div className="flex items-center gap-1">
                    {["chat", "dashboard"].map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setViewMode(mode)}
                        data-testid={`finance-view-${mode}`}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors capitalize
                          ${viewMode === mode
                            ? "bg-[#0020C2] text-white"
                            : "text-gray-500 hover:bg-gray-100"}`}>
                        {mode}
                      </button>
                    ))}
                  </div>

                  {financeAgent.hasDashBoard && viewMode === "dashboard" && dashboardData && (
                    <button
                      onClick={handleDownloadDashboardPdf}
                      disabled={downloadingPdf}
                      data-testid="finance-dashboard-download"
                      className="flex items-center gap-1.5 text-xs font-semibold text-[#0020C2] hover:text-[#001AA3] disabled:opacity-50 px-2 py-1">
                      {downloadingPdf
                        ? <span className="w-3.5 h-3.5 border-2 border-[#0020C2]/30 border-t-[#0020C2] rounded-full animate-spin" />
                        : <DownloadSimple size={14} weight="bold" />}
                      {downloadingPdf ? "Exporting…" : "Download PDF"}
                    </button>
                  )}
                </div>)}

                {viewMode === "chat" ? (
                  <div
                    ref={chatContainerRef}
                    onScroll={handleChatScroll}
                    onWheel={handleUserScrollIntent}
                    onTouchMove={handleUserScrollIntent}
                    className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                    {messages.map((msg) => (
                      <ChatBubble key={msg.id} msg={msg} agentId={financeAgent.id} />
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <div ref={dashboardRef} className="flex-1 overflow-y-auto">
                    <FinanceDashboard data={dashboardData} />
                  </div>
                )}

                <InputBar
                  inputRef={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onSend={() => sendMessage(input)}
                  busy={busy}
                  placeholder="Continue the conversation…"
                />
              </div>
              <div style={{ width: "40%", animation: "financeWizardSlideIn 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
                {ContextPanel}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
